import { useEffect, useState } from "react";
import { Quote, Star, ExternalLink } from "lucide-react";
import { api } from "../lib/api";

/**
 * Public testimonial strip. Pulls real reviews from /api/reviews (added by the
 * admin from /admin/reviews). If the admin hasn't published any yet, the strip
 * renders nothing - better than fake quotes.
 */
export default function TestimonialStrip() {
  const [reviews, setReviews] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get("/reviews").then((r) => setReviews(r.data || [])).finally(() => setLoaded(true));
  }, []);

  if (!loaded || reviews.length === 0) return null;

  // Take the first 3 (sorted by sort_order server-side) so the strip stays compact.
  const visible = reviews.slice(0, 3);

  // Real aggregate Google rating for NBR Group Services (provided by the client).
  const GOOGLE_RATING = 4.8;
  const GOOGLE_REVIEW_COUNT = 57;
  const fillPct = (GOOGLE_RATING / 5) * 100;

  return (
    <section className="py-16 md:py-24 lg:py-28 xl:py-32" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 xl:px-16 2xl:px-24">
        <div className="max-w-2xl mb-12">
          <span className="bnp-chip bnp-chip-light">What our customers say</span>
          <h2 className="font-display text-[clamp(1.75rem,3.2vw,3rem)] text-[#04273d] mt-3 font-semibold tracking-tight">
            Real businesses. Real reviews.
          </h2>

          {/* Aggregate Google star rating (real totals) */}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 mt-4" data-testid="google-rating">
            <span className="font-display font-extrabold text-[#04273d] text-2xl leading-none" data-testid="google-rating-value">{GOOGLE_RATING.toFixed(1)}</span>
            <div className="relative inline-flex" aria-label={`${GOOGLE_RATING} out of 5 stars`}>
              <div className="flex items-center gap-0.5 text-[#cbd5e1]">
                {[1, 2, 3, 4, 5].map((n) => <Star key={n} size={18} className="fill-current" />)}
              </div>
              <div className="flex items-center gap-0.5 text-[#f59e0b] absolute inset-0 overflow-hidden" style={{ width: `${fillPct}%` }}>
                {[1, 2, 3, 4, 5].map((n) => <Star key={n} size={18} className="fill-current shrink-0" />)}
              </div>
            </div>
            <span className="text-sm text-[#447694]">Rated on <span className="font-semibold text-[#04273d]">Google</span> · {GOOGLE_REVIEW_COUNT} reviews</span>
          </div>

          <p className="text-sm text-[#447694] mt-3">All reviews are imported from verified Google customer reviews of NBR Group Services.</p>
          <a
            href="https://g.page/r/CUyIpTfzR_xrEAI/review"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 mt-3 text-sm font-display font-semibold text-[#0d9b80] hover:text-[#04273d] transition-colors"
            data-testid="google-reviews-link"
          >
            See all our reviews on Google <ExternalLink size={14} />
          </a>
        </div>

        <div className="grid md:grid-cols-3 gap-5">
          {visible.map((t) => (
            <article key={t.id} className="bnp-card p-6 md:p-7 flex flex-col" data-testid={`testimonial-card-${t.id}`}>
              <div className="flex items-center justify-between">
                <Quote size={20} className="text-[#71edc9]" />
                <div className="flex" aria-label={`${t.rating} stars`}>
                  {[1,2,3,4,5].map((n) => (
                    <Star key={n} size={14} className={n <= (t.rating || 5) ? "fill-[#f59e0b] text-[#f59e0b]" : "text-[#cbd5e1]"} />
                  ))}
                </div>
              </div>
              <p className="text-[#04273d] text-[15px] leading-relaxed mt-4 flex-1 italic">&ldquo;{t.body}&rdquo;</p>
              <div className="mt-6 pt-5 border-t border-[#9ac7f7]/25">
                <p className="font-display font-semibold text-[#04273d] text-sm leading-tight">{t.author}</p>
                {t.author_role && <p className="font-mono-brand text-[10px] text-[#447694] tracking-wider uppercase mt-0.5">{t.author_role}</p>}
                {t.published_at && <p className="text-[10px] text-[#447694] mt-1">{t.published_at}</p>}
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
